import { useEffect, useState } from "react";
import http from "./httpClient";
import { getLocalValue, LoacalVariables } from "./CommonFunctions";
import { triggerVendorOrdersRefresh } from "./vendorOrdersHub";

// Tracks which orders arrived while the vendor was using the app. A new order stays "new" (its
// card blinks red and the siren sounds) until the vendor accepts it.
//
// - When the app opens, the orders that already exist are recorded as "seen" (not new).
// - After that, an order id that wasn't seen before is new, if it is an order the vendor still has
//   to deal with, and stays new until acceptNewOrder() is called for it.
// - The state lives in sessionStorage, so a page refresh keeps it, and it is kept per user.

interface Tracker {
  seen: Set<string>;
  highlighted: Set<string>;
}

const KEY_PREFIX = "vendorNewOrders:";
const MAX_SEEN_IDS = 2000;

let tracker: Tracker | null = null;
let trackerKey = "";
const subscribers = new Set<() => void>();

const normalize = (value: any) => String(value || "").trim().toLowerCase();

// Lab: an order is waiting for the vendor until a technician is assigned or it is finished
const LAB_NOT_WAITING_STATUSES = [
  "assigned", "sample collected", "samplecollected", "in progress", "inprogress",
  "completed", "delivered", "fulfilled", "report ready", "reportready",
  "cancelled", "canceled", "rejected", "refund", "refunded", "refund initiated", "refundinitiated",
];

export const isLabOrderWaiting = (order: { status?: string; paymentStatus?: string }) =>
  !LAB_NOT_WAITING_STATUSES.includes(normalize(order.status)) &&
  !normalize(order.paymentStatus).startsWith("refund");

// Pharmacy: finished orders live in Order History
const PHARMACY_CLOSED_STATUSES = [
  "delivered", "completed", "fulfilled",
  "cancelled", "canceled", "rejected", "refund", "refunded", "refund initiated", "refundinitiated",
];

export const isPharmacyStatusClosed = (status?: string) => PHARMACY_CLOSED_STATUSES.includes(normalize(status));

const currentKey = () => `${KEY_PREFIX}${getLocalValue(LoacalVariables.UserId) || ""}`;

const loadTracker = (): Tracker | null => {
  const key = currentKey();
  if (tracker && trackerKey === key) return tracker;
  trackerKey = key;
  tracker = null;
  try {
    const parsed = JSON.parse(sessionStorage.getItem(key) || "null");
    if (parsed && Array.isArray(parsed.seen)) {
      tracker = {
        seen: new Set<string>(parsed.seen),
        highlighted: new Set<string>(Array.isArray(parsed.highlighted) ? parsed.highlighted : []),
      };
    }
  } catch {
    // ignore unreadable data
  }
  return tracker;
};

const saveTracker = () => {
  if (tracker) {
    try {
      sessionStorage.setItem(
        trackerKey,
        JSON.stringify({
          seen: Array.from(tracker.seen).slice(-MAX_SEEN_IDS),
          highlighted: Array.from(tracker.highlighted),
        })
      );
    } catch {
      // storage unavailable: new orders just won't stay marked after a refresh
    }
  }
  subscribers.forEach((notify) => notify());
};

export interface OrderEntry {
  id: string;
  // Whether this order should be marked new if it has just arrived
  needsAttention: boolean;
}

// Call with the orders from a fresh load of an order list. Returns how many were newly marked.
// "reliable" means the request succeeded; only a reliable list may start the tracking, because
// a failed request must not make every existing order look new later.
export const ingestOrders = (entries: OrderEntry[], reliable: boolean): number => {
  if (!getLocalValue(LoacalVariables.UserId)) return 0;
  const existing = loadTracker();
  if (!existing) {
    if (!reliable) return 0;
    tracker = { seen: new Set(entries.map((e) => e.id).filter(Boolean)), highlighted: new Set() };
    saveTracker();
    return 0;
  }
  let marked = 0;
  let changed = false;
  entries.forEach(({ id, needsAttention }) => {
    if (id && !existing.seen.has(id)) {
      existing.seen.add(id);
      changed = true;
      if (needsAttention) {
        existing.highlighted.add(id);
        marked += 1;
      }
    }
  });
  if (changed) saveTracker();
  return marked;
};

// The vendor accepted the order: stops its blinking, and the siren once none are left
export const acceptNewOrder = (id: string) => {
  const existing = loadTracker();
  if (existing && existing.highlighted.delete(id)) saveTracker();
};

let syncing = false;

// Loads the vendor's orders and marks the ones not seen before as new. Works from any page, so a
// new order is noticed even while the vendor is on a page that doesn't list orders. The first
// call after the app opens only records what already exists.
export const syncOrderTracking = async () => {
  const userId = getLocalValue(LoacalVariables.UserId);
  if (!userId || syncing) return;
  syncing = true;
  try {
    const entries: OrderEntry[] = [];
    let reliable = false;
    if (getLocalValue(LoacalVariables.UserType) === "pharmacy") {
      const [products, prescriptions]: any[] = await Promise.all([
        http.get(`pharmacy/ph_order_/ByVendor/${userId}`),
        http.get(`pharmacy/PrescriptionOrder/ByVendor/${userId}`),
      ]);
      reliable = Array.isArray(products?.data) && Array.isArray(prescriptions?.data);
      // Same ids and statuses the pharmacy order list uses
      (products?.data || []).forEach((o: any) =>
        entries.push({ id: o.orderId, needsAttention: !isPharmacyStatusClosed(o.status || "Pending") })
      );
      (prescriptions?.data || []).forEach((o: any) =>
        entries.push({
          id: o.id || o.orderId,
          needsAttention: !isPharmacyStatusClosed(o.timeline?.status || o.status || "Pending"),
        })
      );
    } else {
      const bookings: any = await http.get(`lab/LabBooking/lab/${userId}`);
      reliable = Array.isArray(bookings?.data);
      (bookings?.data || []).forEach((o: any) =>
        entries.push({ id: o.bookingId, needsAttention: isLabOrderWaiting(o) })
      );
    }
    // New orders: make any order list that is open load them too
    if (ingestOrders(entries, reliable) > 0) triggerVendorOrdersRefresh();
  } catch {
    // try again on the next check
  } finally {
    syncing = false;
  }
};

export const useNewOrderHighlights = () => {
  const [version, rerender] = useState(0);
  useEffect(() => {
    const notify = () => rerender((n) => n + 1);
    subscribers.add(notify);
    return () => {
      subscribers.delete(notify);
    };
  }, []);

  return {
    isNew: (id: string) => !!loadTracker()?.highlighted.has(id),
    accept: acceptNewOrder,
    newOrderCount: loadTracker()?.highlighted.size || 0,
    // Changes whenever the new-order set changes, for use in dependency lists
    version,
  };
};
